import { z } from 'zod';
import type { DomainEventType } from './events.js';

/**
 * Billing webhooks (G18, ADR-0031) — the public contract for the panel's
 * signed outbound webhook feed. Admins register endpoints and subscribe them
 * to event types; the panel delivers each matching domain event (and periodic
 * usage snapshots) as an HMAC-signed HTTP POST, at-least-once, with retries,
 * a dead-letter state and manual redelivery.
 *
 * The payload shapes for each event live in `events.ts` (they are the same
 * domain events the internal bus carries); this file adds the delivery
 * envelope, the signing contract, the subscribable event catalogue, and the
 * admin DTOs/schemas.
 */

// ── Signing & envelope contract ──────────────────────────────────────

/** Header carrying the delivery id — stable across retries; the receiver's dedupe key. */
export const WEBHOOK_ID_HEADER = 'X-CPM-Webhook-Id';
/** Header carrying the event type. */
export const WEBHOOK_EVENT_HEADER = 'X-CPM-Webhook-Event';
/** Header carrying the Unix-seconds signing timestamp (regenerated per attempt). */
export const WEBHOOK_TIMESTAMP_HEADER = 'X-CPM-Webhook-Timestamp';
/** Header carrying `v1=<hex hmac>` over `"{timestamp}.{rawBody}"`. */
export const WEBHOOK_SIGNATURE_HEADER = 'X-CPM-Webhook-Signature';
/** Header carrying the 1-based attempt number. */
export const WEBHOOK_ATTEMPT_HEADER = 'X-CPM-Webhook-Attempt';

/** Prefix on the signature value, so the scheme can evolve without a flag day. */
export const WEBHOOK_SIGNATURE_VERSION = 'v1';

/**
 * How far a signing timestamp may drift from the receiver's clock before the
 * request should be rejected as a replay. Receivers enforce this; documented
 * so verification code and the panel agree.
 */
export const WEBHOOK_REPLAY_WINDOW_SECONDS = 300;

/**
 * The body of every webhook POST. `version` is `EVENT_SCHEMA_VERSION` (shared
 * with the internal bus); receivers switch on `type` + `version`. `data` is
 * the event-type-specific payload from `DomainEventPayloadMap` (or, for a test
 * ping, `WebhookPingData`).
 */
export interface WebhookEnvelope<T = unknown> {
  /** Delivery id — stable across retries of the same delivery. Dedupe on this. */
  id: string;
  type: WebhookEventType | typeof WEBHOOK_PING_EVENT_TYPE;
  version: number;
  /** ISO-8601 timestamp of the source event / snapshot / ping. */
  createdAt: string;
  data: T;
}

/** The `data` shape of a test ping (the "send a signed ping" button). */
export interface WebhookPingData {
  message: string;
  endpointId: string;
}

/** Envelope `type` for a manual test ping — not a domain event. */
export const WEBHOOK_PING_EVENT_TYPE = 'webhook.ping';

// ── Subscribable event catalogue ─────────────────────────────────────

/**
 * The domain event types billing endpoints may subscribe to (a subset of
 * `DOMAIN_EVENT_TYPES` — `resource.*` is out of billing scope for this goal).
 */
export const WEBHOOK_EVENT_TYPES = [
  'client.created',
  'client.updated',
  'client.deleted',
  'subscription.created',
  'subscription.plan_changed',
  'subscription.suspended',
  'subscription.reactivated',
  'subscription.terminated',
  'plan.created',
  'plan.updated',
  'plan.deleted',
  'quota.exceeded',
  'policy.threshold_crossed',
  'policy.threshold_recovered',
  'subscription.expiry_approaching',
  'subscription.expired',
  'subscription.expiry_extended',
  'subscription.policy_acknowledged',
  'monitoring.incident_opened',
  'monitoring.incident_escalated',
  'monitoring.incident_recovered',
  'monitoring.incident_flapping',
  'security.finding_detected',
  'security.finding_resolved',
  'security.scan_failed',
  'usage.snapshot',
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

// Compile-time proof every webhook event type is a real domain event type.
const _webhookTypesAreDomainEvents: readonly DomainEventType[] = WEBHOOK_EVENT_TYPES;
void _webhookTypesAreDomainEvents;

export interface WebhookEventDescriptor {
  type: WebhookEventType;
  label: string;
  description: string;
  /** UI grouping for the subscription checkboxes. */
  group: string;
}

/** Metadata for the event-subscription UI and docs. */
export const WEBHOOK_EVENT_CATALOG: WebhookEventDescriptor[] = [
  { type: 'client.created', label: 'Client created', description: 'A new tenant account was created.', group: 'Clients' },
  { type: 'client.updated', label: 'Client updated', description: "A tenant's contact data or status changed.", group: 'Clients' },
  { type: 'client.deleted', label: 'Client deleted', description: 'A tenant account was deleted.', group: 'Clients' },
  { type: 'subscription.created', label: 'Subscription created', description: 'A subscription was provisioned.', group: 'Subscriptions' },
  { type: 'subscription.plan_changed', label: 'Subscription plan changed', description: 'A subscription was moved to a different plan.', group: 'Subscriptions' },
  { type: 'subscription.suspended', label: 'Subscription suspended', description: 'A subscription was suspended (staff or quota enforcement).', group: 'Subscriptions' },
  { type: 'subscription.reactivated', label: 'Subscription reactivated', description: 'A suspended subscription was reactivated.', group: 'Subscriptions' },
  { type: 'subscription.terminated', label: 'Subscription terminated', description: 'A subscription was terminated.', group: 'Subscriptions' },
  { type: 'plan.created', label: 'Plan created', description: 'A service plan was created.', group: 'Plans' },
  { type: 'plan.updated', label: 'Plan updated', description: "A service plan's limits, features or status changed.", group: 'Plans' },
  { type: 'plan.deleted', label: 'Plan deleted', description: 'A service plan was deleted.', group: 'Plans' },
  { type: 'quota.exceeded', label: 'Quota exceeded', description: 'A subscription crossed one of its plan limits.', group: 'Usage' },
  { type: 'policy.threshold_crossed', label: 'Policy threshold crossed', description: 'A disk or monthly-transfer soft/hard threshold was crossed.', group: 'Usage' },
  { type: 'policy.threshold_recovered', label: 'Policy threshold recovered', description: 'A policy threshold returned to a clear state.', group: 'Usage' },
  { type: 'subscription.expiry_approaching', label: 'Subscription expiry approaching', description: 'A subscription entered its configured expiry-warning window.', group: 'Subscriptions' },
  { type: 'subscription.expired', label: 'Subscription expired', description: 'A subscription reached its effective expiry date.', group: 'Subscriptions' },
  { type: 'subscription.expiry_extended', label: 'Subscription expiry extended', description: 'An operator extended a subscription expiry date.', group: 'Subscriptions' },
  { type: 'subscription.policy_acknowledged', label: 'Policy incident acknowledged', description: 'An operator acknowledged a lifecycle or threshold incident.', group: 'Subscriptions' },
  { type: 'monitoring.incident_opened', label: 'Monitoring incident opened', description: 'A check crossed its configured failure threshold.', group: 'Monitoring & security' },
  { type: 'monitoring.incident_escalated', label: 'Monitoring incident escalated', description: 'An open incident reached its escalation deadline.', group: 'Monitoring & security' },
  { type: 'monitoring.incident_recovered', label: 'Monitoring incident recovered', description: 'A check satisfied its configured recovery threshold.', group: 'Monitoring & security' },
  { type: 'monitoring.incident_flapping', label: 'Monitoring check flapping', description: 'Repeated state changes triggered alert-noise suppression.', group: 'Monitoring & security' },
  { type: 'security.finding_detected', label: 'Security finding detected', description: 'A workload scan detected a new vulnerability or malware finding.', group: 'Monitoring & security' },
  { type: 'security.finding_resolved', label: 'Security finding resolved', description: 'A previously open workload finding was absent from a successful rescan.', group: 'Monitoring & security' },
  { type: 'security.scan_failed', label: 'Security scan failed', description: 'A scheduled or pre-deployment workload scan could not complete.', group: 'Monitoring & security' },
  { type: 'usage.snapshot', label: 'Usage snapshot', description: 'Periodic per-subscription usage (disk, memory, resource counts).', group: 'Usage' },
];

// ── Admin DTOs ───────────────────────────────────────────────────────

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'delivered', 'failed', 'dead'] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];

/** A registered endpoint, plus a roll-up of its recent delivery health. Never carries the secret. */
export interface WebhookEndpointDto {
  id: string;
  url: string;
  description: string | null;
  eventTypes: WebhookEventType[];
  enabled: boolean;
  maxAttempts: number;
  /** Whether a signing secret is stored (the value itself is write-only). */
  hasSecret: boolean;
  createdAt: string;
  updatedAt: string;
  /** Delivery counts by status across this endpoint's history. */
  deliveryCounts: Record<WebhookDeliveryStatus, number>;
  /** ISO-8601 of the most recent delivery attempt, if any. */
  lastDeliveryAt: string | null;
}

/** One HTTP delivery attempt-track for an endpoint. */
export interface WebhookDeliveryDto {
  id: string;
  endpointId: string;
  eventId: string | null;
  eventType: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  responseStatus: number | null;
  responseDurationMs: number | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/webhooks/deliveries/:id — a delivery with the full signed body and response. */
export interface WebhookDeliveryDetailDto {
  delivery: WebhookDeliveryDto;
  /** The exact envelope that was (or will be) signed and sent. */
  payload: WebhookEnvelope;
  /** Captured response body (truncated), if the endpoint answered. */
  responseBody: string | null;
}

/** GET /api/webhooks/endpoints/:id — an endpoint plus its recent deliveries. */
export interface WebhookEndpointDetailDto {
  endpoint: WebhookEndpointDto;
  deliveries: WebhookDeliveryDto[];
}

/** Returned once on create/rotate — the plaintext secret, never shown again. */
export interface WebhookSecretRevealDto {
  endpoint: WebhookEndpointDto;
  /** The plaintext signing secret. Shown once; store it now. */
  secret: string;
}

/** The periodic usage-snapshot schedule (module setting). */
export interface WebhookSettingsDto {
  usageSnapshotEnabled: boolean;
  usageSnapshotIntervalHours: number;
  lastSnapshotAt: string | null;
}

// ── Validation schemas ───────────────────────────────────────────────

/** Only http(s) endpoints. http is allowed for local receivers; docs push https. */
const webhookUrlSchema = z
  .string()
  .url()
  .refine((u) => /^https?:\/\//i.test(u), 'URL must be http(s)');

const eventTypesSchema = z
  .array(z.enum(WEBHOOK_EVENT_TYPES))
  .min(1, 'Subscribe to at least one event type')
  .refine((types) => new Set(types).size === types.length, 'Duplicate event types');

export const createWebhookEndpointSchema = z.object({
  url: webhookUrlSchema,
  description: z.string().max(500).optional(),
  eventTypes: eventTypesSchema,
  /** Optional caller-supplied secret; generated server-side when omitted. */
  secret: z.string().min(16).max(256).optional(),
  maxAttempts: z.number().int().min(1).max(20).optional(),
  enabled: z.boolean().optional(),
});

export const updateWebhookEndpointSchema = z
  .object({
    url: webhookUrlSchema,
    description: z.string().max(500).nullable(),
    eventTypes: eventTypesSchema,
    maxAttempts: z.number().int().min(1).max(20),
    enabled: z.boolean(),
  })
  .partial();

export const webhookSettingsSchema = z.object({
  usageSnapshotEnabled: z.boolean(),
  usageSnapshotIntervalHours: z.number().int().min(1).max(8760),
});

export type CreateWebhookEndpointInput = z.infer<typeof createWebhookEndpointSchema>;
export type UpdateWebhookEndpointInput = z.infer<typeof updateWebhookEndpointSchema>;
export type WebhookSettingsInput = z.infer<typeof webhookSettingsSchema>;
